import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { psiTokens } from '../../theme/psiTheme';
import ServicesBanner from '../ServicesBanner';

const services = [
  {
    title: 'Six Sigma',
    description:
      'Training and consulting to reduce defects and process variability. PSI offers Yellow, Green & Black Belt certifications, plus real-project mentorship where teams solve a capacity problem using the full Six Sigma methodology.',
  },
  {
    title: 'Lean Manufacturing',
    description:
      'Lean training through classes, workshops, and manufacturing simulators that replicate real production environments. Participants learn by eliminating waste and optimizing flow in hands-on exercises not just theory.',
  },
  {
    title: 'Minitab Training',
    description:
      'Minitab training for engineers and improvement teams: statistical analysis, process charts, and results interpretation applied to real client cases. Focused on data-driven decision making.',
  },
  {
    title: 'Problem Solving',
    description:
      'Training in structured methodologies to solve manufacturing problems from the root cause: QRQC, A3, PDCA, 8D, 7 Quality Tools, FMEA, APQP, and Toyota Kata. PSI trains teams to solve problems right not just fast.',
  },
  {
    title: 'Data Analysis with Excel',
    description:
      'Practical training in statistical and graphical analysis using Excel and Power BI. Ideal for teams that need to make data-based decisions without depending on specialized software. Includes descriptive analysis, control charts, and KPI visualization.',
  },
  {
    title: 'New Employee Onboarding',
    description:
      "PSI develops onboarding content for new team members: generic industrial concepts applicable to any company, plus specific topics based on the client's operations. Ready-to-use deliverable for HR or internal training teams.",
  },
  {
    title: 'Lean Simulators',
    description:
      'PSI offers manufacturing simulators to learn Lean hands-on: participants operate a simulated production line, identify waste, propose improvements, and measure impact. An active learning experience that accelerates Lean culture adoption.',
  },
];

export default function Services() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box
      id="services"
      sx={{
        backgroundColor: psiTokens.background.primary,
        py: isMobile ? 6 : 8,
      }}
    >
      <Container maxWidth="xl">
        {/* Section header */}
        <Box sx={{ mb: 5 }}>
          {/* Accent line */}
          <Box
            sx={{
              width: '40px',
              height: '3px',
              background: `linear-gradient(90deg, ${psiTokens.primary.dark}, ${psiTokens.primary.light})`,
              borderRadius: '2px',
              mb: 2,
            }}
          />

          <Typography
            sx={{
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: psiTokens.primary.light,
              marginBottom: 2,
            }}
          >
            What PSI Offers
          </Typography>

          <Typography
            variant="h2"
            sx={{
              color: psiTokens.text.primary,
              marginBottom: 2,
              fontWeight: 700,
            }}
          >
            Training & Consulting Services
          </Typography>

          <Typography
            sx={{
              fontSize: '15px',
              fontWeight: 400,
              color: psiTokens.text.tertiary,
              lineHeight: 1.8,
              marginBottom: 4,
              maxWidth: '720px',
            }}
          >
            Available on-site, online, or hybrid. In English or Spanish. Adapted to each client's
            context.
          </Typography>
        </Box>

        {/* Services Grid */}
        <Grid container spacing={3} justifyContent="center" sx={{ mb: 5 }}>
          {services.map((service, idx) => (
            <Grid item xs={12} sm={6} md={4} key={idx}>
              <Card
                sx={{
                  height: '100%',
                  backgroundColor: psiTokens.background.secondary,
                  borderRadius: '12px',
                  border: `1px solid ${psiTokens.border}`,
                  borderTop: `4px solid ${psiTokens.primary.dark}`,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <CardContent sx={{ p: 4, flexGrow: 1 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      color: psiTokens.text.primary,
                      marginBottom: 2,
                      fontWeight: 700,
                      fontSize: '20px',
                    }}
                  >
                    {service.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '15px',
                      fontWeight: 400,
                      color: psiTokens.text.secondary,
                      lineHeight: 1.75,
                      marginBottom: 3,
                    }}
                  >
                    {service.description}
                  </Typography>

                  {/* Buttons */}
                  <Stack direction="row" spacing={1.5}>
                    <Button
                      variant="contained"
                      sx={{
                        background: `linear-gradient(90deg, ${psiTokens.primary.dark}, ${psiTokens.primary.light})`,
                        color: '#ffffff',
                        fontSize: '12px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        flex: 1,
                        py: 1,
                      }}
                    >
                      Quote
                    </Button>
                    <Button
                      variant="outlined"
                      sx={{
                        borderColor: psiTokens.primary.light,
                        color: psiTokens.primary.light,
                        fontSize: '12px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        flex: 1,
                        py: 1,
                        '&:hover': {
                          backgroundColor: 'rgba(167, 111, 204, 0.05)',
                        },
                      }}
                    >
                      Learn More
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <ServicesBanner />
      </Container>
    </Box>
  );
}
